import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight, TrendingUp, AlertTriangle } from "lucide-react";

/**
 * Sample-mode UI: a hero banner at the top of the report and a sticky CTA bar
 * at the bottom. Both rendered only when the user lands on /sample-report
 * (sessionStorage flag `ll_sample_mode='1'`).
 */
export function SampleModeBanner({ variant = "healthy" }) {
  const isStressed = variant === "stressed";
  const headline = isStressed
    ? "See how the report flags a struggling business."
    : "Everything you'd get for $29 — visible right here.";
  const subtitle = isStressed
    ? "This sample is a stressed restaurant (DSCR<1, thin cash). Notice the distress flags and the contrast between paid vs free sections."
    : "This sample is a healthy bakery — strong margins, comfortable debt. Try the stressed sample to see distress flags + the full mix of paid and free insights.";
  const labelKicker = isStressed
    ? "Sample report · Roadside Diner & Pub (fictional, stressed)"
    : "Sample report · Sunrise Bakery Co. (fictional, healthy)";

  return (
    <div
      className="no-print mb-7 bg-[#1F382E] text-[#F7F5F0] rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
      data-testid="sample-mode-banner"
      data-variant={variant}
    >
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C26D5C] font-medium">
          {labelKicker}
        </p>
        <p className="font-serif-display text-2xl sm:text-3xl text-[#F7F5F0] tracking-tight leading-snug mt-2">
          {headline}
        </p>
        <p className="text-sm text-[#E5E0D8] leading-relaxed mt-2 max-w-xl">
          {subtitle}
        </p>
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="sample-variant-picker">
          <Link to="/sample-report?variant=healthy">
            <button
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border font-medium transition-all duration-200 ${
                !isStressed
                  ? "bg-[#F7F5F0] text-[#1F382E] border-[#F7F5F0]"
                  : "text-[#E5E0D8] border-[#E5E0D8]/30 hover:bg-[#E5E0D8]/10"
              }`}
              data-testid="variant-healthy-btn"
            >
              <TrendingUp size={12} strokeWidth={2} />
              Healthy
            </button>
          </Link>
          <Link to="/sample-report?variant=stressed">
            <button
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border font-medium transition-all duration-200 ${
                isStressed
                  ? "bg-[#F7F5F0] text-[#1F382E] border-[#F7F5F0]"
                  : "text-[#E5E0D8] border-[#E5E0D8]/30 hover:bg-[#E5E0D8]/10"
              }`}
              data-testid="variant-stressed-btn"
            >
              <AlertTriangle size={12} strokeWidth={2} />
              Stressed
            </button>
          </Link>
        </div>
      </div>
      <div className="flex gap-2 sm:flex-col sm:items-end">
        <Link to="/analyze">
          <Button
            className="bg-[#C26D5C] hover:bg-[#A85A4B] text-[#F7F5F0] rounded-md h-11 px-5 transition-all duration-300 whitespace-nowrap"
            data-testid="sample-banner-cta-analyze"
          >
            Run my numbers
            <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function SampleModeStickyCta() {
  return (
    <div
      className="no-print fixed bottom-0 left-0 right-0 z-30 bg-[#1F382E]/95 backdrop-blur-md border-t border-[#0F1F18] text-[#F7F5F0] shadow-2xl"
      data-testid="sample-mode-sticky"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3.5 flex items-center justify-between gap-4">
        <p className="text-sm text-[#E5E0D8] leading-tight">
          <span className="hidden sm:inline">Liking what you see? </span>
          Get the same report on <span className="font-medium text-[#F7F5F0]">your business</span> in 60 seconds.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/analyze">
            <Button
              className="bg-[#C26D5C] hover:bg-[#A85A4B] text-[#F7F5F0] rounded-md h-10 px-5 transition-all duration-300 text-sm"
              data-testid="sample-sticky-cta-analyze"
            >
              Run my analysis
              <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
